"use client"

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react"
import { useSearchParams } from "next/navigation"
import Link from "next/link"
import { ComparisonContextLink } from "@/components/product/comparison-context-link"
import { comparisonHrefWithContext } from "@/lib/comparison-click-context"
import { readClickContextFromLocation } from "@/lib/click-context-decode"
import { usePostHog } from 'posthog-js/react'
import { ProductShareButtons } from "@/components/blocks/product-share-buttons"
import { CompareEmptyState } from "./compare-empty-state"
import { CompareCard } from "./compare-card"
import { slugFromHref, toCompareRow, type CompareRow } from "./compare-product"
import { ProductCard } from "@/components/homepage-2026/product-card"
import { useSavedCompare, COMPARE_MAX } from "@/components/homepage-2026/saved-compare-store"
import { formatPrice } from "@/components/homepage-2026/format"
import type { HomeProduct } from "@/lib/homepage-2026/types"

// FRIDGE-243: this component renders ONLY the URL-dependent tool area. The
// page shell — header, hero, h1, copy, links, footer — is server-rendered by
// page.tsx OUTSIDE the Suspense boundary, so the static prerender always
// serves it. When this file carried the whole page, useSearchParams() below
// baked the boundary's `fallback={null}` into the artifact and /compare
// served head + chrome + nothing to every non-JS client. The ToolSchemas
// call that lived here was removed with the shell: it was never in the
// served HTML (it sat beneath the bailed boundary), and its WebPage and
// breadcrumb nodes duplicate what CompareSchemas already serves from the
// server — re-adding it server-side would double-emit those nodes.
//
// FRIDGE-872 — rebuilt in the homepage-2026 design language, and with three
// defects fixed that no amount of restyling would have hidden:
//
//   1. THE TOOL SHOWED NOTHING. `/api/products/[slug]` answers
//      `{ product: … }` and this file did `return response.json()`, handing
//      the envelope to the table as the product. Every column read "—" and
//      every header rendered an empty title. The payload is now mapped once,
//      in ./compare-product.ts.
//   2. TICKING "COMPARE" DID NOT REACH THIS PAGE. The tick writes the
//      `fridge_comparison_products` list that the header panel reads; the
//      tool read only `?products=`. The two are now mirrored: an addition to
//      the list appears here, a removal here leaves the list, and the URL is
//      rewritten either way so the comparison stays shareable.
//   3. THERE WAS NO WAY IN. A shopper with nothing selected was shown a grey
//      icon and sent away to find products. The picks rail below puts real
//      products with real Compare ticks on this page.

/** kWh price used for the yearly running-cost estimate beside the energy figure. */
const KWH_DOLLARS = 0.169

interface SpecRow {
  label: string
  render: (product: CompareRow) => ReactNode
  /** Which end of the column wins the orange highlight, and the number it is judged on. */
  best?: { pick: 'min' | 'max'; of: (product: CompareRow) => number | null }
}

function titleCase(value: string): string {
  return value.replace(/[-_]/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
}

const DASH = '—'

const SPEC_ROWS: SpecRow[] = [
  {
    label: 'Best price',
    render: (p) => (p.priceCents && p.priceCents > 0 ? formatPrice(p.priceCents) : 'Check price'),
    best: { pick: 'min', of: (p) => (p.priceCents && p.priceCents > 0 ? p.priceCents : null) },
  },
  { label: 'Retailers tracked', render: (p) => (p.offers.length ? String(p.offers.length) : DASH), best: { pick: 'max', of: (p) => p.offers.length || null } },
  { label: 'Brand', render: (p) => p.brand || DASH },
  { label: 'Model', render: (p) => p.model ?? DASH },
  { label: 'FIN', render: (p) => (p.fin ? <span className="idchip">{p.fin}</span> : DASH) },
  { label: 'Type', render: (p) => (p.productType ? titleCase(p.productType) : DASH) },
  {
    label: 'Capacity',
    render: (p) => (p.capacityCuft ? `${p.capacityCuft} cu. ft.` : DASH),
    best: { pick: 'max', of: (p) => p.capacityCuft },
  },
  {
    label: 'Energy use',
    render: (p) => (p.energyKwhYr ? `${p.energyKwhYr} kWh/yr (~$${Math.round(p.energyKwhYr * KWH_DOLLARS)}/yr)` : DASH),
    best: { pick: 'min', of: (p) => p.energyKwhYr },
  },
  { label: 'Width', render: (p) => (p.widthIn ? `${p.widthIn}"` : DASH) },
  { label: 'Height', render: (p) => (p.heightIn ? `${p.heightIn}"` : DASH) },
  { label: 'Depth', render: (p) => (p.depthIn ? `${p.depthIn}"` : DASH) },
  // Neither end of the scale is "better" — a heavier fridge is not a worse one
  // — so this row carries no highlight. It is here because the payload has
  // always carried `weight` and nothing rendered it, and it is better covered
  // than capacity (1,525 products against 1,578, counted 09/05/2026).
  { label: 'Weight', render: (p) => (p.weightLbs ? `${p.weightLbs} lbs` : DASH) },
  { label: 'Color', render: (p) => (p.color ? titleCase(p.color) : DASH) },
  {
    label: 'ENERGY STAR',
    render: (p) =>
      p.energyStar === true ? (
        <span className="eschip" style={{ alignSelf: 'center' }}>
          <svg className="i" style={{ width: 12, height: 12 }}>
            <use href="#i-leaf" />
          </svg>
          Certified
        </span>
      ) : p.energyStar === false ? (
        'Not certified'
      ) : (
        DASH
      ),
  },
]

// A "Noise level", a "Rating" and a "Reviews" row used to sit at the bottom of
// this list. All three were read out of `specs_json`, and across the 5,708
// active, PDP-eligible products /compare can show, the number carrying any of
// those keys is zero — `products` has no rating or reviews column at all. They
// rendered an em dash for every product, and the Rating and Reviews highlights
// could never fire. The measurement and the full reasoning are recorded at the
// top of ./compare-product.ts, next to the fields that were removed with them.

/** The slug of the column that wins a row, or null when nothing is comparable. */
function bestSlug(products: CompareRow[], row: SpecRow): string | null {
  if (!row.best || products.length < 2) return null
  const scored = products
    .map((p) => ({ slug: p.slug, value: row.best!.of(p) }))
    .filter((s): s is { slug: string; value: number } => typeof s.value === 'number' && Number.isFinite(s.value))
  if (scored.length < 2) return null
  const winner = scored.reduce((a, b) => (row.best!.pick === 'min' ? (a.value <= b.value ? a : b) : a.value >= b.value ? a : b))
  // A tie is not a winner — highlighting one of two identical numbers is a lie.
  if (scored.filter((s) => s.value === winner.value).length > 1) return null
  return winner.slug
}

const BEST_CELL: CSSProperties = {
  background: '#fff4ea',
  boxShadow: 'inset 0 0 0 1px #ffd6b3',
  fontWeight: 700,
  color: '#101828',
}

export default function CompareClient({ picks = [] }: { picks?: HomeProduct[] }) {
  const searchParams = useSearchParams()
  const posthog = usePostHog()
  const sc = useSavedCompare()

  const urlSlugs = useMemo(
    () =>
      (searchParams.get('products') ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, COMPARE_MAX),
    [searchParams]
  )

  const [selected, setSelected] = useState<string[]>(urlSlugs)
  const [rows, setRows] = useState<Record<string, CompareRow>>({})
  const [loading, setLoading] = useState(urlSlugs.length > 0)
  const [error, setError] = useState<string | null>(null)

  // --- the comparison list <-> the saved/compare store -----------------------
  // The store is what the header panel, the product pages and every product
  // card write. Additions to it land here; removals here leave it. Seeded once
  // per mount: on the server the store is always empty, so the seed has to
  // happen after hydration or a deep link would be the only way in.
  const seenIdsRef = useRef<Set<string> | null>(null)
  const slugByIdRef = useRef<Map<string, string>>(new Map())

  useEffect(() => {
    const ids = new Set(sc.compare.map((c) => c.id))
    for (const c of sc.compare) slugByIdRef.current.set(c.id, slugFromHref(c.href))

    if (seenIdsRef.current === null) {
      seenIdsRef.current = ids
      // The seed reads the CURRENT selection rather than the URL it was
      // initialised from: a deep link is an explicit request and must win over
      // the visitor's own stored list, and returning `current` unchanged lets
      // React bail out of the render entirely when there is nothing to seed.
      const seed = sc.compare.map((c) => slugFromHref(c.href)).filter(Boolean).slice(0, COMPARE_MAX)
      setSelected((current) => (current.length === 0 && seed.length > 0 ? seed : current))
      return
    }

    const previous = seenIdsRef.current
    const addedSlugs = sc.compare.filter((c) => !previous.has(c.id)).map((c) => slugFromHref(c.href)).filter(Boolean)
    const droppedSlugs = [...previous].filter((id) => !ids.has(id)).map((id) => slugByIdRef.current.get(id) ?? '').filter(Boolean)
    seenIdsRef.current = ids
    if (addedSlugs.length === 0 && droppedSlugs.length === 0) return

    setSelected((current) => {
      let next = current.filter((slug) => !droppedSlugs.includes(slug))
      for (const slug of addedSlugs) if (!next.includes(slug)) next = [...next, slug]
      return next.slice(-COMPARE_MAX)
    })
  }, [sc.compare])

  // The URL always describes what is on screen, so the comparison stays a
  // shareable link (replaceState, never a navigation — the fetches below are
  // keyed on the selection, not on the address bar).
  useEffect(() => {
    if (typeof window === 'undefined') return
    const canonical = selected.length ? `/compare?products=${encodeURIComponent(selected.join(','))}` : '/compare'
    const want = comparisonHrefWithContext(canonical, readClickContextFromLocation())
    if (window.location.pathname + window.location.search !== want) window.history.replaceState(null, '', want)
  }, [selected])

  // --- product data ---------------------------------------------------------
  const requestedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    // Read the Set ONCE, here, and use that binding in both the body and the
    // cleanup. The ref holds a plain Set that is never reassigned, so this is
    // the same object either way — but capturing it is what tells the
    // exhaustive-deps rule that the cleanup is not reaching for a ref whose
    // contents may have been swapped underneath it.
    const requested = requestedRef.current
    const missing = selected.filter((slug) => !requested.has(slug))
    if (missing.length === 0) {
      setLoading(false)
      return
    }
    for (const slug of missing) requested.add(slug)
    setLoading(true)
    let cancelled = false
    void (async () => {
      const fetched = await Promise.all(
        missing.map(async (slug) => {
          try {
            const response = await fetch(`/api/products/${encodeURIComponent(slug)}`)
            if (!response.ok) return null
            return toCompareRow(await response.json(), slug)
          } catch {
            return null
          }
        })
      )
      if (cancelled) return
      const loaded = fetched.filter((row): row is CompareRow => row !== null)
      if (loaded.length < missing.length) setError('We could not load every product in this comparison.')
      if (loaded.length > 0) {
        setRows((current) => {
          const next = { ...current }
          for (const row of loaded) next[row.slug] = row
          return next
        })
      }
      setLoading(false)
    })()
    return () => {
      cancelled = true
      // The reservation above is what stops the same slug being fetched twice.
      // An abandoned attempt has to GIVE IT BACK: this run's results are
      // discarded by `cancelled`, so if the reservation stood, the next run
      // would compute `missing` as empty, never refetch, and the comparison
      // would stay empty forever with no error to show for it.
      //
      // That is not a hypothetical. React Strict Mode mounts every effect
      // twice, so it happened on EVERY dev load of `/compare?products=`: the
      // first mount reserved both slugs and started the fetch, the cleanup
      // cancelled it, and the second mount found nothing missing. Two 200
      // responses, no error banner, and an empty state. The same loss occurs
      // in production whenever the selection changes while a fetch is in
      // flight — ticking Compare on a second product mid-load, for instance.
      for (const slug of missing) requested.delete(slug)
    }
  }, [selected])

  const products = useMemo(
    () => selected.map((slug) => rows[slug]).filter((row): row is CompareRow => Boolean(row)),
    [selected, rows]
  )

  // One PostHog event per distinct comparison, not one per render.
  const capturedRef = useRef<string>('')
  useEffect(() => {
    if (!posthog || products.length < 2) return
    const signature = products.map((p) => p.slug).join('|')
    if (capturedRef.current === signature) return
    capturedRef.current = signature
    posthog.capture('products_compared', {
      product_count: products.length,
      product_slugs: products.map((p) => p.slug),
      product_brands: products.map((p) => p.brand).filter(Boolean),
      product_types: products.map((p) => p.productType).filter(Boolean),
    })
  }, [posthog, products])

  const removeProduct = useCallback(
    (row: CompareRow) => {
      setSelected((current) => current.filter((slug) => slug !== row.slug))
      requestedRef.current.delete(row.slug)
      // Cards can key the same product by its canonical path instead of its
      // database ID. Remove either identity from the shared list (FRIDGE-928).
      const identity = { id: row.id, href: row.href }
      if (sc.isCompared(identity)) sc.removeCompare(identity)
    },
    [sc]
  )

  const bestPriceSlug = bestSlug(products, SPEC_ROWS[0])
  const selectedHrefSlugs = useMemo(() => new Set(products.map((p) => slugFromHref(p.href))), [products])
  const railProducts = picks.filter((p) => !selectedHrefSlugs.has(slugFromHref(p.href)))
  const full = selected.length >= COMPARE_MAX

  const picksRail =
    railProducts.length > 0 ? (
      <section id="compare-picks" style={{ paddingTop: products.length ? 40 : 28 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', paddingBottom: 4 }}>
          <h2 className="sect-h" style={{ margin: 0 }}>
            {products.length ? 'Add another to the comparison' : 'Start with a popular refrigerator'}
          </h2>
          <Link className="viewall" href="/collections/refrigerators">
            Shop all refrigerators
          </Link>
        </div>
        <div className="sect-sub" style={{ paddingBottom: 14 }}>
          {full
            ? `Four is the maximum — remove one above to make room.`
            : `Tick Compare on any card and it joins the table above. Up to ${COMPARE_MAX} at a time.`}
        </div>
        <div className="rail">
          {railProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </section>
    ) : null

  if (loading && products.length === 0 && selected.length > 0) {
    return (
      <>
        <div
          role="status"
          aria-live="polite"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '48px 0', color: '#6a7282', fontSize: 14 }}
        >
          <span className="livedot" />
          Loading your comparison…
        </div>
        {picksRail}
      </>
    )
  }

  if (products.length === 0) {
    /* Empty State — the same component page.tsx uses as the Suspense
       fallback, so the served HTML and the hydrated zero-selection view
       are identical (FRIDGE-243). */
    return (
      <>
        <CompareEmptyState />
        {picksRail}
      </>
    )
  }

  return (
    <>
      {error ? (
        <div
          style={{ background: '#fff8f1', border: '1px solid #ffd6b3', color: '#7a4a19', borderRadius: 12, padding: '12px 16px', marginBottom: 18, fontSize: 13 }}
        >
          {error}
        </div>
      ) : null}

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', paddingBottom: 14 }}>
        <h2 className="sect-h" style={{ margin: 0 }}>
          Comparing {products.length} of {COMPARE_MAX}
        </h2>
        <button
          type="button"
          className="viewall"
          onClick={() => {
            setSelected([])
            requestedRef.current.clear()
            sc.clearCompare()
          }}
          style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
        >
          Clear the comparison
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gap: 14,
          gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, 240px), 1fr))`,
          alignItems: 'stretch',
        }}
      >
        {products.map((product) => (
          <CompareCard
            key={product.slug}
            product={product}
            isBestPrice={product.slug === bestPriceSlug}
            saved={sc.isSaved(product.id)}
            onRemove={() => removeProduct(product)}
            onToggleSaved={() =>
              sc.toggleSaved({
                id: product.id,
                fin: product.fin ?? '',
                brand: product.brand,
                title: product.title,
                image: product.image ?? '',
                href: product.href,
                priceCents: product.priceCents ?? 0,
              })
            }
          />
        ))}
        {!full ? (
          <a
            href={railProducts.length ? '#compare-picks' : '/collections/refrigerators'}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              minHeight: 200,
              border: '2px dashed #d5dbe3',
              borderRadius: 12,
              color: '#6a7282',
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
              padding: 20,
              textAlign: 'center',
            }}
          >
            <span style={{ fontSize: 26, lineHeight: 1, color: '#ff9951' }} aria-hidden="true">
              +
            </span>
            Add another product
          </a>
        ) : null}
      </div>

      <div style={{ paddingTop: 32 }}>
        <h2 className="sect-h" style={{ margin: 0, paddingBottom: 4 }}>
          Specification by specification
        </h2>
        <div className="sect-sub" style={{ paddingBottom: 14 }}>
          The best number in each row is highlighted in orange. A tie is left unmarked.
        </div>

        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 14, background: '#ffffff' }}>
          <table
            style={{
              width: '100%',
              minWidth: products.length > 1 ? 160 + products.length * 170 : undefined,
              borderCollapse: 'collapse',
              fontSize: 13,
            }}
          >
            <caption className="sr-only">Side-by-side specifications for the products in this comparison</caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  style={{ textAlign: 'left', padding: '12px 14px', background: '#f9fafb', color: '#6a7282', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 150 }}
                >
                  Specification
                </th>
                {products.map((product) => (
                  <th
                    key={product.slug}
                    scope="col"
                    style={{ textAlign: 'center', padding: '12px 14px', background: '#f9fafb', minWidth: 150 }}
                  >
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
                      {product.brand ? <span className="pbrand">{product.brand}</span> : null}
                      <ComparisonContextLink href={product.href} fin={product.fin} style={{ fontSize: 12.5, fontWeight: 600, color: '#101828', lineHeight: 1.3 }}>
                        {product.title}
                      </ComparisonContextLink>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SPEC_ROWS.map((row, index) => {
                const winner = bestSlug(products, row)
                return (
                  <tr key={row.label} style={{ background: index % 2 === 0 ? '#ffffff' : '#fbfcfd' }}>
                    <th
                      scope="row"
                      style={{ textAlign: 'left', padding: '11px 14px', fontWeight: 600, color: '#364152', borderTop: '1px solid #f2f4f7' }}
                    >
                      {row.label}
                    </th>
                    {products.map((product) => (
                      <td
                        key={product.slug}
                        style={{
                          padding: '11px 14px',
                          textAlign: 'center',
                          color: '#101828',
                          borderTop: '1px solid #f2f4f7',
                          ...(product.slug === winner ? BEST_CELL : null),
                        }}
                      >
                        {row.render(product)}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {picksRail}

      <div style={{ marginTop: 32, paddingTop: 22, borderTop: '1px solid #eef1f5' }}>
        <ProductShareButtons productTitle="Compare Products" pageType="comparison" variant="bottom" />
      </div>
    </>
  )
}
